import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { RolePermission, PermissionCheck } from '../types';
import { evaluatePermissions, checkPermission } from './permissions';
import { API_ROUTES } from './api-routes';

/**
 * Permissions Context for React
 * Provides user's permissions to all components via hooks
 */

interface PermissionsContextValue {
  permissions: RolePermission[];
  roleIds: string[];
  isLoading: boolean;
  error: string | null;
  checkAccess: (resourcePath: string, action: 'read' | 'write') => boolean;
  getPermission: (resourcePath: string) => PermissionCheck;
  refetch: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

/**
 * PermissionsProvider
 * Fetches and caches user's permissions from the API
 * Makes permissions available to all child components
 */
export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ROUTES.rbac.permissions.my());
      
      // Detect if we got the Cloudflare Access login page (HTML response indicates auth failure)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        // User is not authenticated - redirect to trigger Cloudflare Access login
        window.location.href = '/';
        return;
      }
      
      if (!response.ok) {
        // Handle 401 Unauthorized - user session expired
        if (response.status === 401) {
          window.location.href = '/';
          return;
        }
        
        const rawError: unknown = await response.json().catch(() => ({ error: response.statusText }));
        let message = response.statusText;
        if (typeof rawError === 'object' && rawError !== null) {
          const errObj = rawError as Record<string, unknown>;
          if (typeof errObj.details === 'string') {message = errObj.details;}
          else if (typeof errObj.error === 'string') {message = errObj.error;}
        }
        throw new Error(`Failed to fetch permissions: ${message}`);
      }

      const data: unknown = await response.json();
      if (typeof data === 'object' && data !== null) {
        const obj = data as Record<string, unknown>;
        if (typeof obj.error === 'string') {
          const details = typeof obj.details === 'string' ? obj.details : obj.error;
          throw new Error(details);
        }

        const rawPerms = Array.isArray(obj.permissions) ? (obj.permissions as unknown[]) : [];
        const perms: RolePermission[] = rawPerms.map((p) => {
          const rp = (p ?? {}) as Record<string, unknown>;
          return {
            id: String(rp.id ?? ''),
            roleId: String(rp.roleId ?? ''),
            resourcePath: String(rp.resourcePath ?? ''),
            canRead: Boolean(rp.canRead),
            canWrite: Boolean(rp.canWrite),
            isBlocklist: Boolean(rp.isBlocklist),
            createdAt: String(rp.createdAt ?? ''),
            createdBy: typeof rp.createdBy === 'string' ? rp.createdBy : undefined,
          };
        });
        setPermissions(perms);

        const rawRoleIds = Array.isArray(obj.roleIds) ? obj.roleIds : [];
        const roleIds = rawRoleIds.filter((r): r is string => typeof r === 'string');
        setRoleIds(roleIds);
      } else {
        // Unexpected shape; fail closed
        setPermissions([]);
        setRoleIds([]);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setPermissions([]);
      setRoleIds([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  // Memoize checkAccess to prevent unnecessary re-renders
  // Only recreate when permissions array changes
  const checkAccess = useCallback((resourcePath: string, action: 'read' | 'write'): boolean => {
    return checkPermission(permissions, resourcePath, action);
  }, [permissions]);

  // Memoize getPermission to prevent unnecessary re-renders
  // Only recreate when permissions array changes
  const getPermission = useCallback((resourcePath: string): PermissionCheck => {
    return evaluatePermissions(permissions, resourcePath);
  }, [permissions]);

  const value: PermissionsContextValue = {
    permissions,
    roleIds,
    isLoading,
    error,
    checkAccess,
    getPermission,
    refetch: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * usePermissions hook
 * Access permissions from any component
 * 
 * @example
 * const { checkAccess } = usePermissions();
 * if (checkAccess('finance/journal', 'write')) {
 *   // Show edit button
 * }
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

/**
 * PermissionGate component
 * Conditionally renders children based on permissions
 * 
 * @example
 * <PermissionGate resource="finance/journal" action="write">
 *   <button>Edit</button>
 * </PermissionGate>
 * 
 * @example
 * <PermissionGate resource="finance/journal" action="write" fallback={<span>Read-only</span>}>
 *   <button>Edit</button>
 * </PermissionGate>
 */
interface PermissionGateProps {
  resource: string;
  action: 'read' | 'write';
  children: ReactNode;
  fallback?: ReactNode;
  showLoading?: boolean;
}

export function PermissionGate({ 
  resource, 
  action, 
  children, 
  fallback = null,
  showLoading = false
}: PermissionGateProps) {
  const { checkAccess, isLoading } = usePermissions();

  if (isLoading && showLoading) {
    return <div className="text-slate-500 text-sm">Loading permissions...</div>;
  }

  if (isLoading && !showLoading) {
    return null;
  }

  const hasAccess = checkAccess(resource, action);
  
  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * ReadOnlyBanner component
 * Shows a banner when user has read but not write access
 */
interface ReadOnlyBannerProps {
  resource: string;
  message?: string;
}

export function ReadOnlyBanner({ resource, message }: ReadOnlyBannerProps) {
  const { getPermission } = usePermissions();
  const perm = getPermission(resource);

  if (!perm.canRead || perm.canWrite) {
    return null; // Don't show if no read access or has write access
  }

  return (
    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-start gap-2">
        <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-yellow-800">Read-Only Access</p>
          <p className="text-sm text-yellow-700 mt-1">
            {message || 'You have read-only access to this section. Contact an administrator to request edit permissions.'}
          </p>
        </div>
      </div>
    </div>
  );
}
